/**
 * The sounds of things happening: crashes, the scrape of metal on the road,
 * glass going, doors, footsteps and the breath of a run, a canopy opening,
 * air brakes. Each is one of the bank's sounds (`core/audio/bank.ts`), its
 * variant, pitch and level picked so that no two in a row are the same.
 */

import { footfalls } from '../../core/audio/ambience.js';
import { hitSound, impactId, stepId } from '../../core/audio/bank.js';
import type { Material, StepSurface } from '../../core/audio/sounds.js';
import type { At, SoundSystem } from './system.js';

/** What a prop is made of, for the sound of hitting it. */
export function propMaterial(species: string): Material {
  switch (species) {
    case 'bin': case 'postbox': return 'bin';
    case 'bench': case 'picket': case 'rail': case 'swing': case 'slide': case 'stump': return 'wood';
    case 'shelter': return 'glass';
    case 'rock': case 'fountain': case 'memorial': case 'gravestone': return 'stone';
    case 'hedge': case 'shrub': case 'dryShrub': case 'tuft': case 'dryTuft': return 'body';
    default:
      // Trees (pine, birch, broadleaf…) are wood; poles, lamps and the rest, metal.
      return /pine|spruce|birch|broadleaf|jungle|palm|acacia|olive|cypress|cactus/i.test(species) ? 'wood' : 'metal';
  }
}

/** Running, how long for: the breath comes harder the longer it goes on. */
const BREATH_AFTER = 4;

export class EffectSound {
  private variant = 0;
  private scraping = 0;
  private runFor = 0;
  private breathIn = true;
  private nextBreath = 0;

  constructor(private readonly sound: SoundSystem) {}

  private next(): number {
    this.variant = (this.variant + 1 + Math.floor(Math.random() * 2)) % 3;
    return this.variant;
  }

  /**
   * A crash: the player's own vehicle giving (`own`, metal or a bike's
   * plastic) and what it hit (`other`), by the change in speed it caused, at
   * `at`. A scrape along it drags on a little after.
   */
  crash(own: Material, other: Material | undefined, dv: number, scrape: number, at: At): void {
    const { size, gain } = hitSound(dv);
    const rate = 0.92 + 0.16 * Math.random();
    if (dv > 0.4) this.sound.play(impactId(own, size, this.next()), { bus: 'effects', at, gain, rate, near: 6 });
    if (other !== undefined && other !== own && dv > 0.4) {
      if (other === 'glass' && size === 'heavy') this.sound.play(`shatter:${this.next() % 2}`, { bus: 'effects', at, gain: gain * 0.9, near: 6 });
      else this.sound.play(impactId(other, size, this.next()), { bus: 'effects', at, gain: gain * 0.7, rate: rate * 1.05, near: 6 });
    }
    // Brief rubbing contact. Require fresh collisions to keep it sounding;
    // a glancing touch must not leave a loud grinding loop after driving away.
    if (scrape > 3.5 && this.scraping <= 0) {
      const seconds = Math.min(0.24, 0.1 + scrape / 160);
      this.scraping = seconds + 0.08;
      this.sound.play('scrape', { bus: 'effects', at, gain: Math.min(0.22, 0.035 + scrape / 100), rate: 0.85 + Math.min(0.2, scrape / 80), seconds, near: 3 });
    }
  }

  /** Something hit that is not the player's: a car in the traffic into another, a prop knocked flying. */
  knock(material: Material, dv: number, at: At): void {
    if (dv <= 0.4) return;
    const { size, gain } = hitSound(dv);
    if (material === 'glass' && size === 'heavy') this.sound.play(`shatter:${this.next() % 2}`, { bus: 'effects', at, gain, near: 8 });
    else this.sound.play(impactId(material, size, this.next()), { bus: 'effects', at, gain, rate: 0.92 + 0.16 * Math.random(), near: 8 });
  }

  /**
   * On foot: a step at each footfall the gait passed between `phaseBefore`
   * and `phaseAfter` on `surface`, and the breath of a run that goes on.
   */
  onFoot(dt: number, phaseBefore: number, phaseAfter: number, speed: number, surface: StepSurface, at: At): void {
    this.scraping = Math.max(0, this.scraping - dt);
    const running = speed > 4;
    this.runFor = running ? this.runFor + dt : Math.max(0, this.runFor - dt * 2);
    if (speed > 0.4) {
      for (let k = 0; k < footfalls(phaseBefore, phaseAfter); k++) {
        this.sound.play(stepId(surface, running, this.next()), {
          bus: 'effects', at: { x: at.x, y: at.y - 1.6, z: at.z }, gain: running ? 0.6 : 0.4, rate: 0.94 + 0.12 * Math.random(), near: 2,
        });
      }
    }
    // Breathing hard after a while running, in and out, quicker the longer it lasts.
    const effort = Math.min(1, Math.max(0, this.runFor - BREATH_AFTER) / 15);
    this.nextBreath -= dt;
    if (effort > 0 && this.nextBreath <= 0) {
      this.sound.play(`breath:${this.breathIn ? 'in' : 'out'}:${this.next() % 2}`, { bus: 'effects', gain: 0.12 + 0.25 * effort, rate: 0.95 + 0.1 * Math.random() });
      // Each breath (some 0.45 s) mostly over before the next.
      this.nextBreath = this.breathIn ? 0.5 - 0.1 * effort : 0.45 - 0.08 * effort;
      this.breathIn = !this.breathIn;
    }
  }

  /** Ticks the scrape's clock when not on foot. */
  tick(dt: number): void {
    this.scraping = Math.max(0, this.scraping - dt);
    this.runFor = Math.max(0, this.runFor - dt * 2);
  }

  door(shut: boolean, at: At, delay = 0): void {
    this.sound.play(shut ? 'door:shut' : 'door:open', { bus: 'effects', at, gain: 0.7, delay, rate: 0.95 + 0.1 * Math.random(), near: 3 });
  }

  canopy(): void {
    this.sound.play('canopy', { bus: 'effects', gain: 0.9 });
  }

  airBrake(at: At): void {
    this.sound.play('airbrake', { bus: 'effects', at, gain: 0.6, rate: 0.9 + 0.2 * Math.random(), near: 8 });
  }

  /**
   * Landing from a jump on `surface`: both feet coming down, the second a
   * moment after the first, louder the harder it landed (`hard` 0..1).
   */
  land(surface: StepSurface, hard: number, at: At): void {
    for (const delay of [0, 0.04 + 0.02 * Math.random()]) {
      this.sound.play(stepId(surface, true, this.next()), {
        bus: 'effects', at: { x: at.x, y: at.y - 1.6, z: at.z }, gain: 0.45 + 0.5 * hard, rate: 0.88 + 0.12 * Math.random(), delay, near: 2,
      });
    }
  }

  /** A person struck, or the player landing hard. */
  body(dv: number, at: At): void {
    this.knock('body', dv, at);
  }
}
